/**
 * Hook para gestión de configuración de usuario
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../modules/auth/hooks/useAuth';
import { UserSettingsService } from '../services/user-settings.service';
import type { UserSettings } from '../types';
import { LoggerService } from '../services/logger.service';

export const useUserSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setSettings(null);
      setLoading(false);
      return;
    }

    const loadSettings = async () => {
      try {
        setLoading(true);
        const userSettings = await UserSettingsService.getOrCreateUserSettings(user.uid);
        setSettings(userSettings);
        setError(null);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Error desconocido');
        setError(error);
        LoggerService.error('Error al cargar configuración', 'useUserSettings', { error: error.message });
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [user?.uid]);

  const updateSettings = async (newSettings: Partial<Pick<UserSettings, 'alertas'>>) => {
    if (!user?.uid) {
      throw new Error('Usuario no autenticado');
    }

    try {
      await UserSettingsService.updateUserSettings(user.uid, newSettings);
      
      // Actualizar estado local
      if (settings) {
        setSettings({
          ...settings,
          ...newSettings,
          updatedAt: new Date(),
        });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error desconocido');
      setError(error);
      LoggerService.error('Error al actualizar configuración', 'useUserSettings', { error: error.message });
      throw error;
    }
  };

  return {
    settings,
    loading,
    error,
    updateSettings,
  };
};
